import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { AccountingAdminPaymentsDto } from '../../accounting-dashboard.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-payments-overview',
  templateUrl: './payments-overview.component.html',
  styleUrls: ['./payments-overview.component.css'],
  imports: [NgApexchartsModule, CommonModule, MatIconModule, MatProgressBarModule, MatMenuModule, MatDividerModule, MatButtonToggleModule, TranslocoPipe]
})
export class PaymentsOverviewComponent implements OnInit, OnChanges {

  @Input() payments: AccountingAdminPaymentsDto[] = [];
  @Input() previousYearPayments: AccountingAdminPaymentsDto[] = [];

  @Input() year = signal<number>(new Date().getFullYear());

  // Fixed order for labels and series positions
  paymentLabels = ['BankTransfer', 'Card', 'Cash', 'Funding'];
  sumByType(payments: AccountingAdminPaymentsDto[] | null | undefined, type: string): number {
    if (!payments?.length) return 0;
    const t = this._normalizePaymentType(type);
    return payments
      .filter(p => this._normalizePaymentType(p.type) === t)
      .reduce((acc, p) => acc + (p.payment || 0), 0);
  }

  changePct(type: string): number {
    const curr = this.sumByType(this.payments, type);
    const prev = this.sumByType(this.previousYearPayments, type);
    if (!prev) return curr ? 100 : 0; // from 0 → something = 100%, else 0%
    return ((curr - prev) / prev) * 100;
  }

  barValue(type: string): number {
    // Progress bar shows current vs previous year (prev = 100 baseline). Clamp to [0..200].
    const pct = this.changePct(type);
    const value = 100 + pct;
    return Math.max(0, Math.min(200, value));
  }

  arrowIcon(type: string): string {
    const pct = this.changePct(type);
    return pct >= 0 ? 'heroicons_mini:arrow-long-up' : 'heroicons_mini:arrow-long-down';
  }

  arrowColorClass(type: string): string {
    return this.changePct(type) >= 0 ? 'text-green-600' : 'text-red-600';
  }

  chartTaskDistribution: ApexOptions = {};

  constructor(private _translocoService: TranslocoService) { }

  ngOnInit(): void {
    console.log(this.paymentLabels);
    this._prepareChartData();
  }

  ngOnChanges(_: SimpleChanges): void {
    this._prepareChartData();
  }

  trackByFn(index: number, item: any): any {
    return item?.id ?? index;
    // payments don’t have ids; using index is fine for static lists
  }

  private _prepareChartData(): void {
    const series = this._countContractsPerType(this.payments, this.paymentLabels);

    var translatedLabels = this.paymentLabels.map(l => this._translocoService.translate(l));
    this.chartTaskDistribution = {
      chart: {
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: '100%',
        type: 'polarArea',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      labels: translatedLabels,
      legend: { position: 'bottom' },
      plotOptions: {
        polarArea: {
          spokes: { connectorColors: 'var(--fuse-border)' },
          rings: { strokeColor: 'var(--fuse-border)' },
        },
      },
      series: series, // <-- counts per label in the same order as labels
      states: {
        hover: { filter: { type: 'darken' } },
      },
      stroke: { width: 2 },
      theme: {
        monochrome: {
          enabled: true,
          color: '#93C5FD',
          shadeIntensity: 0.75,
          shadeTo: 'dark',
        },
      },
      tooltip: {
        followCursor: true,
        theme: 'dark',
      },
      yaxis: {
        labels: {
          style: {
            colors: 'var(--fuse-text-secondary)',
          },
        },
      },
    };
  }

  setYear(value: number | string) {
    if (value === 'last-year') {
      this.year.set(new Date().getFullYear() - 1);
    }
    else if (value === 'this-year') {
      this.year.set(new Date().getFullYear());
    }
  }
  private _countContractsPerType(
    payments: AccountingAdminPaymentsDto[] | null | undefined,
    labels: readonly string[]
  ): number[] {
    const counts = new Array(labels.length).fill(0);

    if (payments) {
      for (const p of payments) {
        const typeName = this._normalizePaymentType(p.type);
        const idx = labels.indexOf(typeName);
        if (idx >= 0) counts[idx]++;
      }
    }

    return counts;
  }

  private _normalizePaymentType(type: string): string {
    // String enum or plain string (ensure consistent casing)
    const normalized = String(type).trim();
    // Optional: standardize common variations if backend ever sends different casing
    switch (normalized.toLowerCase()) {
      case 'bank': return 'Bank';
      case 'card': return 'Card';
      case 'cash': return 'Cash';
      case 'Funding': return 'funding';
      default: return normalized;
    }
  }

  countByType(type: string, mode: 'count' | 'sum' = 'count'): number {
    if (!this.payments?.length) return 0;
    var e = this.payments
      .filter(p => this._normalizePaymentType(p.type) === type)
      .reduce((acc, p) => acc + (mode === 'count' ? 1 : p.payment), 0);

    return e;
  }
}
